// The bell in the header. Today it carries one kind of notification: an offer to
// link a gig you've been invited to into your own calendar. Linking is always
// your choice; nothing is added until you press "Link to my calendar".
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiClient } from "@/App";

const POLL_MS = 60000;

// Gig dates can be missing or malformed; never let that blank the header.
function niceDate(value) {
  if (!value) return "";
  try {
    const d = parseISO(String(value).slice(0, 10));
    return isValid(d) ? format(d, "EEE d MMM") : "";
  } catch {
    return "";
  }
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/notifications");
      setItems(res.data?.notifications || []);
      setUnread(res.data?.unread_count || 0);
    } catch {
      // The bell is a convenience; stay quiet if it can't load.
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [load]);

  const onOpenChange = (next) => {
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      apiClient.post("/notifications/read").catch(() => {});
    }
  };

  const accept = async (n) => {
    setBusyId(n.notification_id);
    try {
      const res = await apiClient.post(`/notifications/${n.notification_id}/accept`);
      setItems((prev) => prev.map((x) =>
        x.notification_id === n.notification_id ? { ...x, status: "accepted", linked_gig_id: res.data.gig_id } : x));
      toast.success("Gig added to your calendar");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't link that gig");
      load();
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (n) => {
    setBusyId(n.notification_id);
    try {
      await apiClient.post(`/notifications/${n.notification_id}/dismiss`);
      setItems((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
    } catch {
      toast.error("Couldn't dismiss that");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread > 0 ? `Notifications, ${unread} new` : "Notifications"}
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-4 text-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0" style={{ width: "min(92vw, 360px)" }} data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-border text-sm font-semibold">Notifications</div>
        {items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="notification-empty">
            You're all caught up.
          </p>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto divide-y divide-border">
            {items.map((n) => {
              const when = [niceDate(n.date), n.start_time].filter(Boolean).join(" · ");
              const where = [n.venue_name, n.city].filter(Boolean).join(", ");
              const accepted = n.status === "accepted";
              return (
                <li key={n.notification_id} className="px-4 py-3 space-y-2" data-testid="notification-item">
                  <p className="text-sm">
                    <span className="font-semibold">{n.from_name}</span> invited you to{" "}
                    <span className="font-semibold">{n.title}</span>
                  </p>
                  {(where || when) && (
                    <p className="text-xs text-muted-foreground">{[where, when].filter(Boolean).join(" — ")}</p>
                  )}
                  {accepted ? (
                    <p className="text-xs flex items-center gap-1 text-muted-foreground">
                      <Check className="h-3.5 w-3.5" />
                      Added to your calendar
                      {n.linked_gig_id && (
                        <>
                          {" · "}
                          <Link to={`/gigs/${n.linked_gig_id}`} className="underline" onClick={() => setOpen(false)}>View</Link>
                        </>
                      )}
                    </p>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busyId === n.notification_id} onClick={() => accept(n)} data-testid="notification-accept">
                        Link to my calendar
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busyId === n.notification_id} onClick={() => dismiss(n)} data-testid="notification-dismiss">
                        Not now
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
